import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, ViewEncapsulation } from '@angular/core';
import { Store } from '@ngrx/store';
import { CUSTOM_EDITOR_BUTTONS_ACTIONS } from '@src/suite/common/enums/constants';
import { EditorActions } from '@src/suite/common/enums/enums';
import { selectEditorApiKey } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { FroalaEditorModule } from 'angular-froala-wysiwyg';
import FroalaEditor from 'froala-editor';

const customActions = new Map<string, string>([
    [EditorActions.COPY, CUSTOM_EDITOR_BUTTONS_ACTIONS[EditorActions.COPY] || EditorActions.COPY],
    [EditorActions.CUT, CUSTOM_EDITOR_BUTTONS_ACTIONS[EditorActions.CUT] || EditorActions.CUT],
    [EditorActions.PASTE, CUSTOM_EDITOR_BUTTONS_ACTIONS[EditorActions.PASTE] || EditorActions.PASTE],
]);
@Component({
    selector: 'st-raw-content-editor',
    imports: [FroalaEditorModule],
    templateUrl: './st-raw-content-editor.html',
    styleUrl: './st-raw-content-editor.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-raw-content-editor',
    },
})
export class RawContentEditorComponent {
    store = inject(Store);
    private readonly editorKey = computed(() => this.store.selectSignal(selectEditorApiKey)());

    private toolbarButtons = ['undo', 'redo', ...customActions.values(), 'fontFamily', 'fontSize', 'bold', 'italic', 'underline', 'textColor', 'alignLeft', 'alignCenter', 'alignRight'];

    public readonly froalaOptions = computed(() => ({
        key: this.editorKey(),
        iconsTemplate: 'material_design',
        toolbarSticky: false,
        toolbarButtons: this.toolbarButtons,
        pastePlain: true,
        placeholderText: 'Add note here...',
        fontFamilySelection: true,
        fontFamilyDefaultSelection: 'Arial',
        imageAllowedTypes: ['jpeg', 'jpg', 'png', 'gif'],
        htmlRemoveTags: ['script', 'style', 'svg', 'iframe', 'video', 'audio', 'input', 'button', 'select', 'option', 'textarea', 'form', 'link', 'meta', 'object', 'embed', 'a'],
        fontFamily: {
            'Roboto,Arial,sans-serif': 'Roboto',
            'Arial,Helvetica,sans-serif': 'Arial',
            'Georgia,serif': 'Georgia',
            "'Times New Roman',Times,serif": 'Times New Roman',
            'Verdana,Geneva,sans-serif': 'Verdana',
        },
        // SECURITY / SANITIZATION OPTIONS
        htmlAllowedTags: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'ul', 'ol', 'li', 'span', 'h1', 'h2', 'h3', 'blockquote', 'img'],
        htmlAllowedAttrs: ['href', 'title', 'target', 'src', 'alt', 'width', 'height', 'class', 'style'],
        fontSizeSelection: true,
        fontSizeDefaultSelection: '14',
        fontSize: ['8', '9', '10', '11', '12', '14', '18', '24', '30', '36', '48', '60', '72', '96'],
        events: {
            initialized: (e: FroalaEditor) => {
                const editor = e.getEditor();
                if (editor) {
                    if (this.disabled()) {
                        editor.edit.off();
                    }
                }
            },
            [customActions.get(EditorActions.COPY)]: async function () {
                const selectedHtml = this?.html?.getSelected();
                if (!selectedHtml) return false;

                // Convert to plain text too (useful for apps that paste as text)
                const tmp = document.createElement('div');
                tmp.innerHTML = selectedHtml;
                const selectedText = tmp.innerText || tmp.textContent || '';

                try {
                    // 2) Write to clipboard (best effort: both HTML + text)
                    if (navigator.clipboard?.write) {
                        await navigator.clipboard.write([
                            new ClipboardItem({
                                'text/html': new Blob([selectedHtml], { type: 'text/html' }),
                                'text/plain': new Blob([selectedText], { type: 'text/plain' }),
                            }),
                        ]);
                    } else {
                        // Fallback: plain text only
                        await navigator.clipboard.writeText(selectedText);
                    }

                    // 3) Trigger Froala lifecycle event so internal handlers run
                    // this.events.trigger('window.copy', [], true);
                    return true;
                } catch (e) {
                    console.error('Error copying to clipboard:', e);
                    return false;
                }
            },
            [customActions.get(EditorActions.PASTE)]: async function () {
                if (!navigator.clipboard || !navigator.clipboard.readText) {
                    console.warn('Clipboard API not available, fallback to Ctrl+V');
                    return;
                }
                // 1) Read clipboard (must be inside a user gesture: click/keydown)
                let html = '';
                let text = '';

                if (navigator.clipboard?.read) {
                    const items = await navigator.clipboard.read();
                    for (const item of items) {
                        if (item.types.includes('text/html')) {
                            html = await (await item.getType('text/html')).text();
                            break;
                        }
                        if (item.types.includes('text/plain')) {
                            text = await (await item.getType('text/plain')).text();
                        }
                    }
                } else if (navigator.clipboard?.readText) {
                    text = await navigator.clipboard.readText();
                }

                // 1) get text/HTML from clipboard
                // let clipboardHtml = await navigator.clipboard.readText();

                let clipboardHtml = html || text;
                if (!clipboardHtml) return false;

                // 2) run your shared beforeCleanup logic if any
                // clipboardHtml = processClipboardHtml(clipboardHtml);

                // 3) run Froala’s HTML cleaner (same family of logic as paste cleaning)
                clipboardHtml = this.clean.html(clipboardHtml);

                // 4) let others hook into "afterCleanup" if they want
                //    (if you have handlers for 'paste.afterCleanup', they’ll see this)
                const afterCleanupResult = this.events.trigger('paste.afterCleanup', [clipboardHtml]);
                if (typeof afterCleanupResult === 'string') {
                    clipboardHtml = afterCleanupResult;
                }

                // 5) insert into editor
                this.html.insert(clipboardHtml, false);

                // 6) fire a final "paste.after" notification
                this.events.trigger('paste.after');
            },
            [customActions.get(EditorActions.CUT)]: async function () {
                const selectedHtml: string = this.html.getSelected();
                if (!selectedHtml) return false;

                // Plain-text fallback from HTML
                const tmp = document.createElement('div');
                tmp.innerHTML = selectedHtml;
                const selectedText = (tmp.innerText || tmp.textContent || '').trim();

                try {
                    // Best: write both HTML + plain text (supported in modern Chromium; may vary by browser)
                    if (navigator.clipboard?.write) {
                        await navigator.clipboard.write([
                            new ClipboardItem({
                                'text/html': new Blob([selectedHtml], { type: 'text/html' }),
                                'text/plain': new Blob([selectedText], { type: 'text/plain' }),
                            }),
                        ]);
                    } else {
                        // Fallback: plain text only
                        await navigator.clipboard.writeText(selectedText);
                    }

                    // Make it undoable
                    this.undo.saveStep();

                    // Delete selection by replacing it with empty string
                    this.html.insert('', true);

                    this.undo.saveStep();

                    return true;
                } catch {
                    return false;
                }
            },
            copy: function () {
                this.events.trigger(customActions.get(EditorActions.COPY), [], true);
                return false;
            },
            cut: function () {
                this.events.trigger(customActions.get(EditorActions.CUT), [], true);
                return false;
            },
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'paste.before': function () {
                this.events.trigger(customActions.get(EditorActions.PASTE), []);
                return false;
            },
        },
    }));

    // inputs
    readonly editorContent = input<string>();
    public readonly disabled = input(false, {
        transform: coerceBooleanProperty,
    });

    // outputs
    readonly contentChange = output<string>();

    // methods
    contentChanged(content: Event) {
        this.contentChange.emit(content as unknown as string);
    }
}
